import type { IntResolver } from '@pct-ts/typing';
import { isInt } from '@pct-ts/typeguard';

/**
 * Resolves an integer value from a resolver function or static integer.
 *
 * This function accepts either a direct integer value or a function that returns an integer.
 * If an integer is provided, it returns that value unchanged. If a function is provided,
 * it calls the function with the given parameter to generate the integer value.
 *
 * @param resolver - An integer value or a function that returns an integer
 * @param param - The parameter to pass to the resolver function
 * @returns The resolved integer value
 *
 * @example
 * // Using static integer
 * const static = resolveInt(42, {});
 * console.log(static); // Output: 42
 *
 * @example
 * // Using resolver function
 * const dynamic = resolveInt(
 *   (user: { age: number }) => user.age,
 *   { age: 25 }
 * );
 * console.log(dynamic); // Output: 25
 */
export function resolveInt<T>(resolver: IntResolver<T>, param: T): number {
  if (isInt(resolver)) {
    return resolver;
  }
  return resolver(param);
}
